import React, { useState, useEffect } from "react";
import CurrentApplicationView from "@/components/PreScreen/CurrentApplicationView";
import { applicationService } from "@/services/applicationService";
import useToastStore from "@/components/Common/Toast/toastStore";
import { useQueryClient } from "@tanstack/react-query";
import { applicationQueryKeys } from "@/hooks/queries/useApplication";
import ApplicationStatusHistory from "@/components/applications/ApplicationStatusHistory";

const ApplicationViewModal = ({ showModal, onClose, applicant }) => {
  if (!showModal) return null;

  // States needed for CurrentApplicationView
  const [showCV, setShowCV] = useState(true);
  const [tagsPerApplication, setTagsPerApplication] = useState({});
  const [scoresPerApplication, setScoresPerApplication] = useState({});
  const [notesPerApplication, setNotesPerApplication] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const queryClient = useQueryClient();
  const { showToast } = useToastStore;


  // Fetch available tags
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const { data: response } = await applicationService.AllTags();
        setAvailableTags(response.tags || []);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };
    fetchAllTags();
  }, []);

  // Initialize data for this application
  useEffect(() => {
    if (applicant) {
      // Initialize tags
      setTagsPerApplication((prev) => ({
        ...prev,
        [applicant.id]:
          applicant?.application_tags?.map((tag) => tag.name) || [],
      }));

      // Initialize scores from scorecards
      setScoresPerApplication((prev) => {
        if (!prev[applicant.id]) {
          const initialScores =
            applicant?.scorecards?.map((score) => ({
              vacancy_scorecard_id: score.id,
              score: score.score,
            })) || [];

          return {
            ...prev,
            [applicant.id]: initialScores,
          };
        }
        return prev;
      });

      // Initialize notes
      setNotesPerApplication((prev) => ({
        ...prev,
        [applicant.id]: applicant?.application_notes || [],
      }));
    }
  }, [applicant]);

  const handleAddTag = async (application_id, tags) => {
    if (tags.trim()) {
      const newTagsArray = tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);

      const existingTags = tagsPerApplication[applicant.id] || [];
      const updatedTags = [...existingTags, ...newTagsArray];

      // Update local state
      setTagsPerApplication((prev) => ({
        ...prev,
        [applicant.id]: updatedTags,
      }));

      // Store tags
      try {
        const mappedTags = {
          applications: [
            {
              application_id: parseInt(application_id),
              tag_names: updatedTags,
            },
          ],
        };

        await applicationService.BulkTagsUpdate(mappedTags);
        showToast({
          message: "Tags updated successfully",
          type: "success"
        });
        queryClient.invalidateQueries({
          queryKey: [applicationQueryKeys.details(applicant.vacancy_id)],
        });
      } catch (error) {
        console.error("Failed to update tags:", error);
        showToast({
          message: "Failed to update tags",
          type: "error"
        });
        // Rollback local state on error
        setTagsPerApplication((prev) => ({
          ...prev,
          [applicant.id]: existingTags,
        }));
      }
    }
  };

  const handleRemoveTag = async (indexToRemove) => {
    const existingTags = tagsPerApplication[applicant.id] || [];
    const updatedTags = existingTags.filter(
      (_, index) => index !== indexToRemove
    );

    // Update local state
    setTagsPerApplication((prev) => ({
      ...prev,
      [applicant.id]: updatedTags,
    }));

    // Store updated tags
    try {
      const mappedTags = {
        applications: [
          {
            application_id: parseInt(applicant.id),
            tag_names: updatedTags,
          },
        ],
      };

      await applicationService.BulkTagsUpdate(mappedTags);
      showToast({
        message: "Tags updated successfully",
        type: "success"
      });
      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(applicant.vacancy_id)],
      });
    } catch (error) {
      console.error("Failed to update tags:", error);
      showToast({
        message: "Failed to update tags",
        type: "error"
      });
      // Rollback local state on error
      setTagsPerApplication((prev) => ({
        ...prev,
        [applicant.id]: existingTags,
      }));
    }
  };

  const handleScoreChange = async (applicationId, scores, totalScore) => {
    setScoresPerApplication((prev) => ({
      ...prev,
      [applicationId]: scores,
    }));

    try {
      const mappedScores = {
        applications: [
          {
            application_id: parseInt(applicationId),
            scores: scores.map((score) => ({
              vacancy_score_id: score.vacancy_scorecard_id,
              score: score.score,
            })),
          },
        ],
      };

      await applicationService.BulkScoreCardsUpdate(mappedScores);
      showToast({
        message: "Scores updated successfully",
        type: "success"
      });
      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(applicant.vacancy_id)],
      });
    } catch (error) {
      console.error("Failed to update scores:", error);
      showToast({
        message: "Failed to update scores",
        type: "error"
      });
    }
  };

  const handleAddNote = async (applicationId, note) => {
    const newNotes = [...(notesPerApplication[applicationId] || []), note];

    setNotesPerApplication((prev) => ({
      ...prev,
      [applicationId]: newNotes,
    }));

    try {
      const mappedNotes = {
        applications: [
          {
            application_id: parseInt(applicationId),
            comments: newNotes.map((note) => ({
              comment: typeof note === "object" && note.note ? note.note : note,
            })),
          },
        ],
      };

      await applicationService.BulkNotesUpdate(mappedNotes);
      showToast({
        message: "Note added successfully",
        type: "success"
      });
      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(applicant.vacancy_id)],
      });
    } catch (error) {
      console.error("Failed to add note:", error);
      showToast({
        message: "Failed to add note",
        type: "error"
      });
    }
  };

  const handleEditNote = async (applicationId, note, index) => {
    const updatedNotes = notesPerApplication[applicationId].map(
      (existingNote, noteIndex) => (noteIndex === index ? note : existingNote)
    );

    setNotesPerApplication((prev) => ({
      ...prev,
      [applicationId]: updatedNotes,
    }));

    try {
      const mappedNotes = {
        applications: [
          {
            application_id: parseInt(applicationId),
            comments: updatedNotes.map((note) => ({
              comment: typeof note === "object" && note.note ? note.note : note,
              comment_id:
                typeof note === "object" && note.note_id
                  ? note.note_id
                  : undefined,
            })),
          },
        ],
      };

      await applicationService.BulkNotesUpdate(mappedNotes);
      showToast({
        message: "Note updated successfully",
        type: "success"
      });
      queryClient.invalidateQueries({
        queryKey: [applicationQueryKeys.details(applicant.vacancy_id)],
      });
    } catch (error) {
      console.error("Failed to update note:", error);
      showToast({
        message: "Failed to update note",
        type: "error"
      });
    }
  };

  const handleShowHistory = (application) => {
    setSelectedApplication(application);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedApplication(null);
  };
  console.log(applicant);
  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-[99] bg-black bg-opacity-75 overflow-y-auto"
        onClick={onClose}
      >
        <div
          className="bg-card rounded-lg shadow-lg p-6 w-[90%] max-w-7xl relative my-8"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Applicant Details</h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="max-h-[80vh] overflow-y-auto">
            <CurrentApplicationView
              currentApplication={applicant}
              currentVacancy={applicant?.vacancy}
              tagsPerApplication={tagsPerApplication}
              availableTags={availableTags}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              showCV={showCV}
              onToggleCV={() => setShowCV(!showCV)}
              onScoreChange={handleScoreChange}
              onAddNote={handleAddNote}
              onEditNote={handleEditNote}
              onShowHistory={handleShowHistory}
              scoresPerApplication={scoresPerApplication}
              notesPerApplication={notesPerApplication}
            />
          </div>
        </div>
      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[100] bg-black bg-opacity-75"
          onClick={handleCloseHistoryModal}
        >
          <div
            className="bg-card rounded-lg shadow-lg p-6 w-[40%] max-md:w-[60%] relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Status History</h3>
            <div className="max-h-[60vh] overflow-y-auto">
              <ApplicationStatusHistory applicant={selectedApplication} />
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={handleCloseHistoryModal}
                className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApplicationViewModal;
